/**
 * Window property the head script sets: whether the tab was hidden while the
 * HTML was being parsed.
 */
export const INITIAL_VISIBILITY_GLOBAL = '__initialVisibilityHidden'

/**
 * Inline head script that runs while the HTML is parsed, before the Nuxt
 * bundle boots. It only records the tab's visibility in one window property
 * and never throws.
 */
export const INITIAL_VISIBILITY_SCRIPT = `(function(){try{window.${INITIAL_VISIBILITY_GLOBAL}=document.visibilityState==='hidden'}catch(e){}})()`

const isPrerendering = (): boolean => {
  const doc: Document & { prerendering?: boolean } = document
  return doc.prerendering === true
}

const wasPrerendered = (): boolean => {
  const [navigation] = performance.getEntriesByType('navigation')
  const activationStart: unknown =
    navigation && Reflect.get(navigation, 'activationStart')
  return typeof activationStart === 'number' && activationStart > 0
}

const firstVisibilityState = (): string | undefined => {
  // Chrome records the page's visibility at navigation start as the first
  // `visibility-state` entry. Other browsers return no entries.
  const [first] = performance.getEntriesByType('visibility-state')
  return first?.name
}

const headScriptHidden = (): boolean | undefined => {
  const hidden: unknown = Reflect.get(window, INITIAL_VISIBILITY_GLOBAL)
  return typeof hidden === 'boolean' ? hidden : undefined
}

/**
 * Whether the landing page is known to have loaded in view. A prerendered page
 * counts as in view, since it is hidden only until it is shown. Otherwise the
 * browser's own record of the visibility at navigation start decides, then the
 * head script's. Without either the visibility is unknown, which counts as not
 * in view. Never throws.
 */
export function loadedInView(): boolean {
  try {
    if (isPrerendering() || wasPrerendered()) return true
    const initial = firstVisibilityState()
    if (initial === 'visible' || initial === 'hidden') {
      return initial === 'visible'
    }
    const hidden = headScriptHidden()
    return hidden === undefined ? false : !hidden
  } catch {
    return false
  }
}
